import { getRef, isEnumSchema, isObjectSchema, isRefSchema, isRestResolver, parseRef, refItemToShapeSchema, serializeRef } from '@takeshape/schema';
import { value } from '@takeshape/util';
import isString from 'lodash/isString.js';
import omit from 'lodash/omit.js';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'BigCommerce Catalog';
// Spec from takeshape/openapi-specs repo
// To regenerate: node scripts/combine-bigcommerce-catalog.mjs in the openapi-specs repo
export const bigcommerceCatalogSpec = 'https://raw.githubusercontent.com/takeshape/openapi-specs/b30a389/specs/bigcommerce/catalog.v3.yml';
function getStoreHash(apiPath) {
    return /bigcommerce.com\/stores\/([A-Za-z0-9]+)\//.exec(apiPath)?.[1];
}
function removeArgs(objectSchema, names) {
    objectSchema.properties = omit(objectSchema.properties, names);
    objectSchema.required &&= objectSchema.required.filter((name) => !names.includes(name));
}
/**
 * Get the enum values from an include arg's referenced shape.
 * The include arg has format: { type: 'array', items: { '@ref': 'serviceId:EnumShapeName' } }
 */
function getIncludeEnumValues(context, includeArg) {
    const ref = includeArg?.items && getRef(context, includeArg.items);
    const shapeSchema = ref && refItemToShapeSchema(context, ref);
    const enumValues = shapeSchema && isEnumSchema(shapeSchema) ? shapeSchema.enum : undefined;
    return Array.isArray(enumValues) ? enumValues.filter((v) => typeof v === 'string') : undefined;
}
/**
 * Build the fields array for includeFromSelection directive.
 * Since BigCommerce field names match include values, each entry is just the field name (string).
 */
function buildIncludeFields(enumValues) {
    return enumValues;
}
function isEmptyObject(prop) {
    return !prop || !isObjectSchema(prop) || Object.keys(prop.properties ?? {}).length === 0;
}
/**
 * Check if a shape is a simple data/meta wrapper that can be unwrapped.
 * Returns the data property schema if unwrappable, undefined otherwise.
 *
 * A shape is unwrappable if:
 * - It has exactly "data" property, or "data" and "meta" properties
 * - The meta property (if present) has no meaningful properties
 */
function getUnwrappableDataProp(context, shapeRef) {
    const shapeSchema = refItemToShapeSchema(context, parseRef(context, shapeRef));
    if (isObjectSchema(shapeSchema)) {
        const numProps = Object.keys(shapeSchema.properties).length;
        const { data, meta } = shapeSchema.properties;
        // Check if the shape has exactly "data" or "data" and an empty "meta"
        if ((numProps === 1 && data) || (numProps === 2 && data && meta && isEmptyObject(meta))) {
            return data;
        }
    }
}
/**
 * Unwrap queries that return a data/meta wrapper to return the inner data type directly.
 * Only unwraps when data is a direct ref (not an array).
 */
function unwrapDataWrapper(context, query) {
    const dataProp = typeof query.shape === 'string' && getUnwrappableDataProp(context, query.shape);
    const dataRef = dataProp && isRefSchema(dataProp) && getRef(context, dataProp);
    if (dataRef) {
        query.shape = serializeRef(dataRef);
        if (isRestResolver(query.resolver)) {
            query.resolver.options = { ...query.resolver.options, dataPath: 'data' };
        }
    }
}
/**
 * Transform product queries to use includeFromSelection directive instead of manual include arg.
 * This allows the include parameter to be automatically inferred from the GraphQL selection set.
 */
function transformProductQuery(context, queryName) {
    const query = context.queries[queryName];
    if (!query || !isObjectSchema(query.args) || !query.args.properties.include) {
        return;
    }
    // Get the allowed include values from the enum schema
    const enumValues = getIncludeEnumValues(context, query.args.properties.include);
    if (!enumValues?.length) {
        return;
    }
    // Remove the include arg from query args
    removeArgs(query.args, ['include']);
    const { resolver } = query;
    if (!isRestResolver(resolver) || !resolver.searchParams?.ops) {
        return;
    }
    // Replace the include op with the includeFromSelection directive
    const includeOpIndex = resolver.searchParams.ops.findIndex((op) => op.path === 'include');
    if (includeOpIndex === -1) {
        return;
    }
    // Check if the query shape still points to a data/meta wrapper
    // If so, we need itemsPath to navigate into the data field for selections
    const stillHasWrapper = typeof query.shape === 'string' && getUnwrappableDataProp(context, query.shape) !== undefined;
    const directiveConfig = [
        [
            'includeFromSelection',
            {
                ...(stillHasWrapper && { itemsPath: 'data' }),
                fields: buildIncludeFields(enumValues)
            }
        ]
    ];
    resolver.searchParams.ops[includeOpIndex] = {
        path: 'include',
        mapping: directiveConfig
    };
}
export function schemaTransform(layerSchema, serviceConfig) {
    // Create context with services for ref resolution
    const context = {
        ...layerSchema,
        services: { [serviceConfig.id]: serviceConfig }
    };
    // Unwrap data/meta wrappers for all queries
    for (const query of Object.values(context.queries)) {
        unwrapDataWrapper(context, query);
    }
    // Transform getProduct and getProducts queries to use includeFromSelection
    transformProductQuery(context, 'getProduct');
    transformProductQuery(context, 'getProducts');
    return layerSchema;
}
const serviceProvider = {
    name: 'bigcommerceCatalog',
    title,
    description: 'Access BigCommerce Catalog API for managing products, categories, brands, variants, and modifiers.',
    categories: ['ecommerce'],
    links: [
        { title: 'Homepage', url: 'https://www.bigcommerce.com/' },
        { title: 'Docs', url: 'https://developer.bigcommerce.com/docs/rest-catalog' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('bigcommerce-catalog'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'apiPath', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'BigCommerce Catalog'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'bigcommerce-catalog'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'BigCommerceCatalog'
            },
            apiPath: {
                title: 'API Path',
                type: 'string',
                pattern: '^https://api.bigcommerce.com/stores/[A-Za-z0-9]+/v3/?$'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    clientId: {
                        title: 'Client ID',
                        type: 'string',
                        '@sensitive': true
                    },
                    clientSecret: {
                        title: 'Client Secret',
                        type: 'string',
                        '@sensitive': true
                    },
                    accessToken: {
                        title: 'Access Token',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['clientId', 'clientSecret', 'accessToken']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'apiPath', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            apiPath: {
                widget: 'singleLineText',
                instructions: 'ex: https://api.bigcommerce.com/stores/5frbmvc2tl/v3/ from BigCommerce API Account.'
            },
            authentication: {
                properties: {
                    clientId: {
                        widget: 'sensitiveField',
                        instructions: 'Client ID from BigCommerce API Account.'
                    },
                    clientSecret: {
                        widget: 'sensitiveField',
                        instructions: 'Client Secret from BigCommerce API Account. Required for webhook verification.'
                    },
                    accessToken: {
                        widget: 'sensitiveField',
                        instructions: 'Access Token from BigCommerce API Account with Catalog read/write permissions.'
                    }
                },
                order: ['clientId', 'clientSecret', 'accessToken']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            id: formData.id,
            title: formData.title ?? title,
            provider: 'bigcommerceCatalog',
            serviceType: 'openapi',
            authenticationType: 'custom',
            namespace: formData.namespace ?? 'BigCommerceCatalog',
            options: {
                spec: bigcommerceCatalogSpec,
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        if (isString(formData?.apiPath)) {
            const storeHash = getStoreHash(formData.apiPath);
            if (storeHash) {
                config.options = {
                    ...config.options,
                    endpoint: `https://api.bigcommerce.com/stores/${storeHash}/v3/`,
                    apiPath: formData.apiPath
                };
            }
        }
        return updateAuthentication(config, formData, { type: 'custom' });
    },
    getCustomAuthInfo(authentication) {
        // BigCommerce REST API uses X-Auth-Token header for authentication
        return [[['headers', 'X-Auth-Token'], authentication.accessToken]];
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    schemaTransform,
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        // Brands endpoint for health check - lightweight and commonly available
        endpoint: endpointWithPath('/catalog/brands')
    })
};
export default serviceProvider;
